import { EyeIcon, EyeOffIcon } from "lucide-react";
import React from "react";

import { IgButton } from "@intelligems/ig-theme";
import { useTheme } from "@mui/material";
import { useDispatch, useSelector } from "@src/store";
import { ExperienceBuilderAction } from "@src/store/slices/experienceBuilder";
import { getHiddenVariationMods } from "@src/store/slices/experienceBuilder/common/selectors/getHiddenVariationMods";
import { getVariationMods } from "@src/store/slices/experienceBuilder/common/selectors/getVariationMods";

/**
 * The Mod list header's hide-all/show-all control.
 *
 * One button, not two: the direction is derived from the hidden count, so a
 * partly hidden list offers "Show all" and one click returns to showing
 * everything. Hidden at zero mods, where neither transition means anything.
 *
 * `willHide` is the direction of the NEXT click, not the current state of
 * anything — in a partly hidden state it is `false` while some mods are still
 * hidden. Keeping the two senses apart is why it is not called `hidden`.
 *
 * The click passes down exactly what was rendered, so it cannot disagree with
 * the label it was rendered with. Ids are the previewed variation's only, which
 * is what leaves other variations' hidden ids untouched.
 */
export const AllVisibilityButton = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const variationMods = useSelector(getVariationMods);
  const hiddenMods = useSelector(getHiddenVariationMods);

  if (variationMods.length === 0) return null;

  const willHide = hiddenMods.length === 0;
  const Icon = willHide ? EyeOffIcon : EyeIcon;

  return (
    <IgButton
      variant="text"
      size="small"
      startIcon={<Icon size={16} color={theme.igIconColors.light} />}
      onClick={() =>
        void dispatch(
          ExperienceBuilderAction.setModsHidden({
            modificationIds: variationMods.map((mod) => mod.id),
            hidden: willHide,
          })
        )
      }
    >
      {willHide ? "Hide all" : "Show all"}
    </IgButton>
  );
};
